/* eslint-disable no-console */
import React, { useState, useEffect } from "react";
import dayjs from "dayjs";
import { useDispatch, useSelector } from "react-redux";
import { Drawer } from "antd";
import getRequestDetailColors from "../getRequestDetailColors";
import CloseIcon from "../../../Common/Svgs/CloseIcon";
import Detailistings from "../DetailListings";
import { REQUEST_STATUS } from "../constants";
import { updateRequest } from "../../../Redux/Slices/requests.slice";
import StepHeader from "../../Trips/TripDetails/components/StepHeader";
import { AssignmentCard } from "../../Trips/TripDetails/components/AssignmentTypeSelector";
import WeightDeliveredIcon from "../../../Common/Svgs/WeightDeliveredIcon";
import WeightCollectedIcon from "../../../Common/Svgs/WeightCollectedIcon";
import TripAssignment from "./components/TripAssignment";
import { client } from "../../../Utils/axiosClient";
import { getRequestList } from "../../../Redux/APIs/requestsAPI";

export const feedRequestSteps = {
  REQUEST_REJECTED: 0,
  REQUEST_CREATED: 1,
  SELECT_ASSIGNMENT_TYPE: 2,
  SELECT_AGENT: 3,
  SELECT_HUB: 4,
  REQUEST_ACCEPTED_HUB: 5,
};

const LOCAL_DEFAULT_LIMIT = 10;

const FeedRequestDetailsDrawer = ({
  visible,
  onCancel,
  currentPage,
  setCurrentPage,
}) => {
  const dispatch = useDispatch();

  const { currentRequest } = useSelector((state) => state.requests);

  const { borderColor, bgColor } = getRequestDetailColors(currentRequest?.type);

  const [step, setStep] = useState(feedRequestSteps.REQUEST_CREATED);
  const [deliveryPrice, setDeliveryPrice] = useState("");
  const [selectedTrip, setSelectedTrip] = useState(null);

  useEffect(() => {
    const statusNum = Number(currentRequest?.status || 0);
    if (statusNum === REQUEST_STATUS.REJECTED) {
      setStep(feedRequestSteps.REQUEST_REJECTED);
    } else if (statusNum === REQUEST_STATUS.ACCEPTED) {
      setStep(feedRequestSteps.REQUEST_ACCEPTED_HUB);
    } else {
      setStep(feedRequestSteps.REQUEST_CREATED);
    }
  }, [currentRequest]);

  const handleAcceptRequest = async (deliveryType = "hub") => {
    try {
      dispatch(
        updateRequest({
          key: "loading",
          value: true,
        }),
      );

      await client.post(
        `${process.env.REACT_APP_API_URL}/ussd/accept-request`,
        {
          requestId: Number(currentRequest?.id || 0),
          requestType: currentRequest?.type,
          deliveryType,
          deliveryPrice,
          tripId: selectedTrip,
        },
      );

      setStep(feedRequestSteps.REQUEST_ACCEPTED_HUB);

      const response = await dispatch(
        getRequestList({
          offset: ((currentPage || 1) - 1) * LOCAL_DEFAULT_LIMIT,
        }),
      );

      if (!response.payload?.result?.length && currentPage > 1) {
        const previousPage = currentPage - 1;
        setCurrentPage(previousPage);

        await dispatch(
          getRequestList({
            offset: (previousPage - 1) * LOCAL_DEFAULT_LIMIT,
          }),
        );
      }

      dispatch(
        updateRequest({
          key: "showToastMessage",
          value: {
            visible: true,
            data: {
              title: "1 Request Accepted.",
              description: `Request for ${
                currentRequest?.metaData?.commodityName ||
                currentRequest?.metaData?.commodity_name ||
                "commodity"
              } was accepted successfully. SMS sent to farmer.`,
            },
          },
        }),
      );
      onCancel();
    } catch (error) {
      console.log(error);
    } finally {
      dispatch(
        updateRequest({
          key: "loading",
          value: false,
        }),
      );
    }
  };

  const getFooter = () => {
    if (step === feedRequestSteps.SELECT_ASSIGNMENT_TYPE) return null;
    return (
      <div className="absolute bottom-6 w-[91.5%] h-4 flex items-center gap-x-3">
        <button
          type="button"
          className="flex-1 flex items-center justify-center py-3 h-[42px] bg-light-grey hover:bg-medium-grey rounded-md"
          onClick={onCancel}
        >
          <p className="font-poppins font-medium text-xs leading-[18px] text-black">
            Close
          </p>
        </button>

        {step === feedRequestSteps.REQUEST_CREATED && (
          <button
            type="button"
            className="flex-1 flex items-center justify-center py-3 h-[42px] bg-alert-red hover:bg-alert-red-hover rounded-md"
            onClick={() =>
              dispatch(
                updateRequest({
                  key: "rejectModal",
                  value: { visible: true, type: "ussd" },
                }),
              )
            }
          >
            <p className="font-poppins font-medium text-xs leading-[18px] text-white">
              Reject
            </p>
          </button>
        )}

        {step === feedRequestSteps.REQUEST_CREATED && (
          <button
            type="button"
            className="flex-1 flex items-center justify-center py-3 h-[42px] bg-primary hover:bg-primary-50 rounded-md"
            onClick={() => setStep(feedRequestSteps.SELECT_ASSIGNMENT_TYPE)}
          >
            <p className="font-poppins font-medium text-xs leading-[18px] text-white">
              Accept
            </p>
          </button>
        )}

        {(step === feedRequestSteps.SELECT_AGENT ||
          step === feedRequestSteps.SELECT_HUB) && (
          <button
            type="button"
            className="flex-1 flex items-center justify-center py-3 h-[42px] bg-primary hover:bg-primary-50 rounded-md"
            onClick={() =>
              handleAcceptRequest(
                step === feedRequestSteps.SELECT_AGENT ? "trip" : "hub",
              )
            }
          >
            <p className="font-poppins font-medium text-xs leading-[18px] text-white">
              Assign & Accept
            </p>
          </button>
        )}
      </div>
    );
  };

  const getBody = () => {
    if (step === feedRequestSteps.REQUEST_ACCEPTED_HUB) {
      const rawDeliveryType =
        currentRequest?.metaData?.deliveryType ||
        currentRequest?.metaData?.delivery_type;

      const isTrip = rawDeliveryType === "trip";

      const deliveryTypeLabel = isTrip
        ? "Deliver via Trip"
        : "Pickup at Hub Station";

      const rawDeliveryPrice =
        deliveryPrice ||
        currentRequest?.metaData?.deliveryPrice ||
        currentRequest?.metaData?.delivery_price;

      const deliveryPriceLabel = rawDeliveryPrice
        ? `${rawDeliveryPrice} Ksh`
        : "--";

      const assignedTripId =
        currentRequest?.metaData?.tripId ||
        currentRequest?.metaData?.trip_id ||
        selectedTrip ||
        "--";

      return (
        <>
          <div className="mt-6 py-4 px-6 bg-green-10 rounded-xl">
            <div className="max-w-max bg-background-white py-1 px-2 border border-green rounded-md">
              <p className="font-poppins font-medium text-xs leading-[18px] text-green">
                Accepted
              </p>
            </div>

            <div className="mt-4 flex">
              <Detailistings
                title="Accepted By"
                value={`${currentRequest?.updatedByFirstName || "Admin"} ${
                  currentRequest?.updatedByLastName || ""
                }`}
              />

              <Detailistings
                title="Date & Time"
                value={
                  currentRequest?.updatedAt
                    ? dayjs(currentRequest.updatedAt).format(
                        "MMM DD, YYYY, hh:mm A",
                      )
                    : dayjs().format("MMM DD, YYYY, hh:mm A")
                }
              />
            </div>
          </div>
          <div
            className="mt-6 rounded-xl border py-4 px-6"
            style={{
              background: "#FFF",
              borderColor: "#DFDFDF",
            }}
          >
            <div
              className="py-1 px-2 h-[20px] max-w-max rounded-md"
              style={{
                background: "#085054",
              }}
            >
              <p className="font-ail font-semibold text-[10px] leading-none text-white">
                Delivery Details
              </p>
            </div>

            <div className="mt-4 flex items-start gap-x-4">
              <Detailistings title="Delivery Type" value={deliveryTypeLabel} />

              {isTrip ? (
                <Detailistings title="Trip ID" value={assignedTripId} />
              ) : (
                <Detailistings
                  title="Hub Station"
                  value={
                    currentRequest?.hubName ||
                    currentRequest?.metaData?.hubName ||
                    currentRequest?.metaData?.hub_name ||
                    "--"
                  }
                />
              )}
            </div>

            <div className="mt-4 flex items-start gap-x-4">
              <Detailistings
                title="Delivery Price"
                value={deliveryPriceLabel}
              />
            </div>
          </div>
        </>
      );
    }

    if (step === feedRequestSteps.REQUEST_REJECTED) {
      return (
        <div className="mt-6 py-4 px-6 bg-alert-10 rounded-xl">
          <div className="max-w-max bg-background-white py-1 px-2 border border-alert-red rounded-md">
            <p className="font-poppins font-normal text-xs leading-[18px] text-alert-red">
              Rejected
            </p>
          </div>

          <div className="mt-4 flex">
            <Detailistings
              title="Rejected By"
              value={`${currentRequest?.updatedByFirstName || ""} ${
                currentRequest?.updatedByLastName || ""
              }`}
            />
            <Detailistings
              title="Date & Time"
              value={
                currentRequest?.updatedAt
                  ? dayjs(currentRequest?.updatedAt).format(
                      "MMM DD, YYYY, hh:mm A",
                    )
                  : "--"
              }
            />
          </div>

          <p className="mt-4 font-poppins font-normal text-xs leading-[18px] text-dark-gray">
            Reason
          </p>

          <div className="mt-1 p-2 bg-background-white rounded-md font-poppins font-medium text-sm text-black first-letter:capitalize">
            {currentRequest?.metaData?.reasonText ||
              currentRequest?.metaData?.reason_text ||
              "N/A"}
          </div>
        </div>
      );
    }

    return null;
  };

  const requestedBy = currentRequest?.requestedBy || "--";
  const contactNo =
    currentRequest?.metaData?.mobileNumber ||
    currentRequest?.metaData?.mobile_number ||
    "--";
  const hubName =
    currentRequest?.hubName ||
    currentRequest?.metaData?.hubName ||
    currentRequest?.metaData?.hub_name ||
    "--";
  const commodityName =
    currentRequest?.metaData?.commodityName ||
    currentRequest?.metaData?.commodity_name ||
    "--";
  const deliveryDate =
    currentRequest?.metaData?.deliveryDate ||
    currentRequest?.metaData?.delivery_date ||
    "--";
  const timeSlot =
    currentRequest?.metaData?.timeSlot ||
    currentRequest?.metaData?.time_slot ||
    "--";

  const isUnknownFarmer =
    !currentRequest?.metaData?.farmerId && !currentRequest?.metaData?.farmer_id;

  return (
    <Drawer
      width={560}
      placement="right"
      closable={false}
      maskStyle={{
        background: "transparent",
        boxShadow: "12px 12px 16px -4px #10182814",
      }}
      open={visible}
      onClose={onCancel}
      className="rounded-tl-xl"
    >
      <div className="p-6 w-full h-full bg-background-white rounded-tl-xl relative">
        <div className="flex items-center justify-between">
          <p className="font-poppins font-medium text-base text-black">
            Request Details
          </p>

          <CloseIcon
            className="w-5 h-5 cursor-pointer stroke-dark-grey-text"
            onClick={onCancel}
          />
        </div>

        {(step === feedRequestSteps.REQUEST_CREATED ||
          step === feedRequestSteps.REQUEST_ACCEPTED_HUB ||
          step === feedRequestSteps.REQUEST_REJECTED) && (
          <div
            className="mt-6 rounded-xl border py-5 px-6"
            style={{
              background: bgColor,
              borderColor,
            }}
          >
            <div
              className="py-1 px-2 h-[26px] max-w-max rounded-md"
              style={{
                background: borderColor,
              }}
            >
              <p className="font-poppins font-medium text-xs leading-[18px] text-background-white">
                Agri Commodity
              </p>
            </div>

            <div className="mt-5 flex items-start gap-x-4">
              <Detailistings title="Requested By" value={requestedBy} />
              <Detailistings title="Contact No." value={contactNo} />
            </div>

            <div className="mt-5 flex items-start gap-x-4">
              <Detailistings title="Hub Station" value={hubName} />
              <Detailistings title="Commodity Type" value={commodityName} />
            </div>
            <div className="mt-5 flex items-start gap-x-4">
              <Detailistings
                title="Selected Delivery Date"
                value={deliveryDate}
              />
              <Detailistings title="Selected Delivery Slot" value={timeSlot} />
            </div>
          </div>
        )}
        {step === feedRequestSteps.SELECT_ASSIGNMENT_TYPE && (
          <div className="mt-4 pt-4 border-t border-light-grey">
            <StepHeader
              title="Select feed delivery type"
              onBack={() => setStep(feedRequestSteps.REQUEST_CREATED)}
            />

            <div className="space-y-4">
              {!isUnknownFarmer && (
                <AssignmentCard
                  title="Deliver via Trip"
                  description="Select to deliver feed to the farmer via a trip"
                  icon={<WeightDeliveredIcon className="text-white" />}
                  bgClass="bg-[linear-gradient(112.74deg,_#1A7075_0%,_#045358_100%)]"
                  textClass="text-white"
                  subTextClass="text-[#E7E7E7]"
                  arrowClass="text-white"
                  onClick={() => setStep(feedRequestSteps.SELECT_AGENT)}
                />
              )}

              <AssignmentCard
                title="Pickup at Hub Station"
                description="Select to allow the farmer to pickup feed from a hub station"
                icon={<WeightCollectedIcon className="text-black" />}
                bgClass="bg-[linear-gradient(112.44deg,_#FFDD72_2.37%,_#FFD553_98.43%)]"
                textClass="text-black"
                subTextClass="text-[#6F6F6F]"
                arrowClass="text-[#6F6F6F]"
                onClick={() => setStep(feedRequestSteps.SELECT_HUB)}
              />
            </div>
          </div>
        )}
        {step === feedRequestSteps.SELECT_AGENT && (
          <div className="mt-6 pt-6 border-t border-light-grey">
            <TripAssignment
              mode="agent"
              setStep={setStep}
              deliveryPrice={deliveryPrice}
              setDeliveryPrice={setDeliveryPrice}
              selectedTrip={selectedTrip}
              setSelectedTrip={setSelectedTrip}
            />
          </div>
        )}
        {step === feedRequestSteps.SELECT_HUB && (
          <div className="mt-6 pt-6 border-t border-light-grey">
            <TripAssignment
              mode="hub"
              setStep={setStep}
              deliveryPrice={deliveryPrice}
              setDeliveryPrice={setDeliveryPrice}
              selectedTrip={selectedTrip}
              setSelectedTrip={setSelectedTrip}
            />
          </div>
        )}

        {getBody()}
        {getFooter()}
      </div>
    </Drawer>
  );
};

export default FeedRequestDetailsDrawer;
